import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { DiscountRuleApiService } from '../services/discount-rule-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import {
  DiscountRuleResponse,
  verifyCouponResponse,
} from '../models/discount-rule.model';
import { DiscountRuleDetailModalComponent } from '../discount-rule-detail-modal/discount-rule-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ProductoApiService } from '../services/producto-api.service';

@Component({
  selector: 'app-discount-rule-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    DiscountRuleDetailModalComponent,
  ],
  templateUrl: './discount-rule-list.component.html',
})
export class DiscountRuleListComponent implements OnInit {
  rules: DiscountRuleResponse[] = [];
  loading = false;
  error = '';
  businessId = 1;

  showDetailModal = false;
  selectedRule: DiscountRuleResponse | null = null;

  // Filters
  showFilters = false;
  filtroRuleType: string | undefined;
  filtroCouponCode = '';

  // Coupon tester
  showCouponTester = false;
  couponCode: string | undefined;
  couponProductId: number | undefined;
  couponMaxUses: number | undefined;
  couponResult: verifyCouponResponse | null = null;
  testingCoupon = false;
  couponError = '';

  productsById = new Map<number, string>();

  constructor(
    private ruleApi: DiscountRuleApiService,
    private authService: AuthService,
    private productApi: ProductoApiService,
    private datetimeService: DatetimeService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;
  }

  ngOnInit(): void {
    this.cargarReglas();
  }

  cargarReglas(): void {
    this.loading = true;
    this.error = '';
    this.ruleApi
      .searchDiscountRule({
        businessId: this.businessId,
        ruleType: this.filtroRuleType,
        couponCode: this.filtroCouponCode || undefined,
      })
      .subscribe({
        next: (data) => {
          this.rules = data;
          this.loading = false;
          this.resolverProductos();
        },
        error: (err) => {
          this.rules = [];
          this.loading = false;
          this.error = err.error?.message || 'Error al cargar reglas';
        },
      });
  }

  buscar(): void {
    this.cargarReglas();
  }

  limpiarFiltro(): void {
    this.filtroRuleType = undefined;
    this.filtroCouponCode = '';
    this.cargarReglas();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  toggleCouponTester(): void {
    this.showCouponTester = !this.showCouponTester;
  }

  nuevaRegla(): void {
    this.router.navigate(['/productos/reglas-descuento/crear']);
  }

  editarRegla(rule: DiscountRuleResponse): void {
    this.router.navigate(['/productos/reglas-descuento/editar', rule.id]);
  }

  verDetalle(rule: DiscountRuleResponse): void {
    this.selectedRule = rule;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedRule = null;
  }

  async eliminarRegla(rule: DiscountRuleResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar regla',
      message: '¿Eliminar esta regla de descuento?',
    });
    if (!ok) return;
    this.ruleApi.deleteDiscountRule(rule.id, this.businessId).subscribe({
      next: () => {
        this.cargarReglas();
        this.toast.deleted('Regla de descuento');
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar';
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar',
        });
      },
    });
  }

  getDiscountType(): string {
    if (!this.couponResult?.discount_type) '-';
    return this.couponResult?.discount_type === 'fixed_amount'
      ? 'Monto Fijo'
      : 'Porcentaje';
  }

  getFormatedPrice(price: number): string {
    if (!price || !this.couponResult) return '0';
    if (price <= 0) return `0 ${this.couponResult.currency_code}`;
    return `${this.couponResult.prefix}${price} ${this.couponResult.currency_code}`;
  }

  resolverValueCoupon(coupon: verifyCouponResponse): string {
    if (!coupon.discount_value) return '0';
    if (coupon.discount_type === 'percent' && coupon.discount_value > 0) {
      return `${coupon.discount_value}%`;
    }
    return this.getFormatedPrice(coupon.discount_value);
  }

  resolverLimitedCouponDate(coupon: verifyCouponResponse): string {
    if (!coupon.valid_from && !coupon.valid_until) return 'Ilimitado';
    if (!coupon.valid_from && coupon.valid_until)
      return `Válido hasta ${this.formatDate(coupon.valid_until)}`;
    if (coupon.valid_from && !coupon.valid_until)
      return `Disponible desde ${this.formatDate(coupon.valid_from)}`;
    return `${this.formatDate(coupon.valid_from)} - ${this.formatDate(coupon.valid_until)}`;
  }

  getCouponStatusLabel(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'Activo';
      case 'NOT_STARTED':
        return 'Programado';
      case 'EXPIRED':
        return 'Expirado';
      case 'MAX_USES_REACHED':
        return 'Agotado';
      default:
        return 'Inactivo';
    }
  }

  probarCupon(): void {
    if (!this.couponCode) return;

    this.testingCoupon = true;
    this.couponResult = null;
    this.couponError = '';

    this.ruleApi.verifyCoupon(this.couponCode?.trim()).subscribe({
      next: (result) => {
        this.couponResult = result;
        this.testingCoupon = false;
        this.couponError = '';
      },
      error: (err) => {
        this.couponError =
          err.error?.message || 'Cupón inválido o no aplicable';
        this.testingCoupon = false;
      },
    });
  }

  getRuleTypeLabel(ruleType: string): string {
    const labels: Record<string, string> = {
      volume: 'Volumen',
      contractual: 'Contractual',
      coupon: 'Cupón',
      min_price: 'Precio mínimo',
    };
    return labels[ruleType] || ruleType;
  }

  getDiscountLabel(rule: DiscountRuleResponse): string {
    if (rule.discountType === 'percent' && rule.discountValue != null)
      return `${rule.discountValue}%`;
    if (rule.discountType === 'fixed_amount' && rule.discountValue != null)
      return this.formatPrecio(rule.discountValue);
    if (rule.couponCode) return `Cupón: ${rule.couponCode}`;
    if (rule.minSalePrice != null)
      return `Min. ${this.formatPrecio(rule.minSalePrice)}`;
    return '-';
  }

  formatPrecio(price?: number): string {
    if (price === undefined || price === null) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  }

  private resolverProductos(): void {
    const ids = [
      ...new Set(
        this.rules
          .filter((r) => r.productId && !r.productName)
          .map((r) => r.productId as number),
      ),
    ];
    ids.forEach((id) => {
      if (this.productsById.has(id)) return;
      this.productApi.getProductById(id, this.businessId).subscribe({
        next: (p) => this.productsById.set(id, p.name),
        error: () => this.productsById.set(id, `Producto #${id}`),
      });
    });
  }

  getProductLabel(rule: DiscountRuleResponse): string {
    if (rule.productName) return rule.productName;
    if (rule.productId)
      return (
        this.productsById.get(rule.productId) ?? `Producto #${rule.productId}`
      );
    return '-';
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '-';
    const date = dateStr;
    const localDate = date ? `${date}T12:00:00` : null;

    return this.datetimeService.formatDateShort(localDate);
  }

  isFutureDate(date?: string): boolean {
    if (!date) return false;
    const [year, month, day] = date.split('-').map(Number);

    const target = new Date(year, month - 1, day);
    target.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return target > today;
  }

  resolveStatus(rule: DiscountRuleResponse): { status: string; color: string } {
    if (rule.isExpired)
      return {
        status: 'Expirado',
        color:
          'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300',
      };
    if (rule.usesRemaining === 0 && rule.maxUses)
      return {
        status: 'Agotado',
        color:
          'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300',
      };

    if (this.isFutureDate(rule.validFrom))
      return {
        status: 'Programado',
        color:
          'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
      };
    return {
      status: 'Activo',
      color:
        'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300',
    };
  }
}
